import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import CheckoutModal from '../components/CheckoutModal';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { AddOn, Offer, PaymentMode, Vendor } from '../lib/database.types';
import { SEATING_STYLES } from '../lib/database.types';
import { formatMoney, formatTime } from '../lib/format';

export default function Checkout() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [searchParams] = useSearchParams();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [headcount, setHeadcount] = useState(10);
  const [bookingDate, setBookingDate] = useState(searchParams.get('date') || '');
  const [bookingTime, setBookingTime] = useState('17:30');
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('prepaid');
  const [selectedAddOnKeys, setSelectedAddOnKeys] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [insertDone, setInsertDone] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState<string | null>(null);

  useEffect(() => {
    if (!offerId) return;
    (async () => {
      const { data: o } = await supabase.from('ga_offers').select('*').eq('id', offerId).maybeSingle();
      if (!o) return;
      setOffer(o);
      // Prefer the headcount the user picked on the dashboard / location page;
      // fall back to the offer's minimum.
      const headcountParam = Number(searchParams.get('headcount'));
      setHeadcount(
        headcountParam && headcountParam >= o.headcount_min && headcountParam <= o.headcount_max
          ? headcountParam
          : o.headcount_min,
      );
      setBookingTime(o.window_start.slice(0, 5));
      const { data: v } = await supabase.from('ga_vendors').select('*').eq('id', o.vendor_id).maybeSingle();
      setVendor(v);
    })();
  }, [offerId, searchParams]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!offer || !vendor || !user) return;
    setError(null);

    if (headcount < offer.headcount_min || headcount > offer.headcount_max) {
      setError(`Headcount must be between ${offer.headcount_min} and ${offer.headcount_max}.`);
      return;
    }
    if (!bookingDate) {
      setError('Pick a date.');
      return;
    }

    setSubmitting(true);
    setModalOpen(true);
    setInsertDone(false);

    const orgRow = await supabase
      .from('ga_org_members')
      .select('org_id')
      .eq('user_id', user.id)
      .maybeSingle();
    const orgId = orgRow.data?.org_id ?? null;

    // Prepaid charges the full headcount up front. Rsvp_based authorises now
    // and we collect the actual yes-count at the 3pm cutoff (Stripe wiring
    // for both modes lands in a follow-up; for now the demo path just records
    // the intent so the success screen and emails can render correctly).
    const pickedAddOns = (offer.add_ons ?? []).filter((a) => selectedAddOnKeys.has(a.key));
    const addOnsPerGuest = pickedAddOns.reduce((sum, a) => sum + a.price_cents_per_guest, 0);
    const addOnsTotal = addOnsPerGuest * headcount;
    const total = offer.price_cents * headcount + addOnsTotal;

    const { data, error } = await supabase
      .from('ga_bookings')
      .insert({
        offer_id: offer.id,
        vendor_id: vendor.id,
        organizer_user_id: user.id,
        organization_id: orgId,
        booking_date: bookingDate,
        booking_time: `${bookingTime}:00`,
        headcount,
        total_cents: total,
        status: 'confirmed',
        payment_status: paymentMode === 'prepaid' ? 'paid' : 'authorized',
        payment_mode: paymentMode,
        payment_ref: 'demo',
        notes: notes || null,
        add_ons: pickedAddOns,
        add_ons_total_cents: addOnsTotal,
      })
      .select()
      .single();

    setSubmitting(false);

    if (error || !data) {
      // Pull the curtain back so the user can see and fix the error.
      setModalOpen(false);
      setError(error?.message ?? 'Could not create booking.');
      return;
    }

    setCreatedBookingId(data.id);
    setInsertDone(true);
  }

  function onModalFinished() {
    if (createdBookingId) {
      navigate(`/bookings/${createdBookingId}/success`, { replace: true });
    }
  }

  if (!offer || !vendor) {
    return (
      <>
        <Nav />
        <div style={{ padding: '120px 0', textAlign: 'center', color: 'var(--ink-soft)' }}>Loading…</div>
      </>
    );
  }

  const availableAddOns: AddOn[] = offer.add_ons ?? [];
  const pickedAddOns = availableAddOns.filter((a) => selectedAddOnKeys.has(a.key));
  const addOnsPerGuest = pickedAddOns.reduce((sum, a) => sum + a.price_cents_per_guest, 0);
  const baseTotal = offer.price_cents * headcount;
  const addOnsTotal = addOnsPerGuest * headcount;
  const total = baseTotal + addOnsTotal;
  const seatingLabel = SEATING_STYLES.find((s) => s.value === offer.seating_style)?.label ?? null;
  const ctaLabel = paymentMode === 'prepaid'
    ? `Pay ${formatMoney(total)} now →`
    : `Hold the table — pay at 3pm →`;
  function toggleAddOn(key: string) {
    setSelectedAddOnKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  return (
    <>
      <Nav />
      <section style={{ paddingTop: 56, paddingBottom: 96 }}>
        <div className="container-narrow">
          <div className="eyebrow" style={{ marginBottom: 18 }}>Reserve · {vendor.name}</div>
          <h1 className="display" style={{ fontSize: 'clamp(40px, 5vw, 64px)', margin: 0 }}>
            Lock in <span className="serif-italic" style={{ color: 'var(--wine)' }}>{offer.title.toLowerCase()}</span>.
          </h1>
          <p style={{ color: 'var(--ink-soft)', marginTop: 18, maxWidth: 540 }}>
            Pick a payment style, invite the team after. They don't need a Goaround account, just an email.
          </p>

          <form onSubmit={onSubmit} style={{ marginTop: 40, padding: 32, borderRadius: 18, background: 'var(--card)' }}>
            <div className="stack">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <label>
                  <span className="field-label">Date</span>
                  <input type="date" value={bookingDate} onChange={(e) => setBookingDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} required />
                </label>
                <label>
                  <span className="field-label">Start time</span>
                  <input type="time" value={bookingTime} onChange={(e) => setBookingTime(e.target.value)} min={offer.window_start.slice(0, 5)} max={offer.window_end.slice(0, 5)} required />
                </label>
              </div>
              <div>
                <span className="field-label">Headcount ({offer.headcount_min}–{offer.headcount_max})</span>
                <div className="row" style={{ gap: 16 }}>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setHeadcount(Math.max(offer.headcount_min, headcount - 1))}>–</button>
                  <input type="number" value={headcount} min={offer.headcount_min} max={offer.headcount_max} onChange={(e) => setHeadcount(Number(e.target.value))} style={{ textAlign: 'center', maxWidth: 120 }} />
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setHeadcount(Math.min(offer.headcount_max, headcount + 1))}>+</button>
                </div>
              </div>
              <label>
                <span className="field-label">Notes for the bar (optional)</span>
                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Dietary needs, anyone arriving late, etc." />
              </label>

              {(availableAddOns.length > 0 || seatingLabel) && (
                <>
                  <hr className="divider" />
                  <div>
                    <div className="row-between" style={{ alignItems: 'center', marginBottom: 10 }}>
                      <div className="mono" style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
                        Make it your own
                      </div>
                      {seatingLabel && (
                        <span className="tag tag-soft" style={{ fontSize: 11 }}>Setup · {seatingLabel}</span>
                      )}
                    </div>
                    {availableAddOns.length === 0 ? (
                      <p className="muted" style={{ fontSize: 13, margin: 0 }}>No add-ons for this offer.</p>
                    ) : (
                      <div className="ga-addon-list">
                        {availableAddOns.map((a) => {
                          const on = selectedAddOnKeys.has(a.key);
                          return (
                            <button
                              key={a.key}
                              type="button"
                              onClick={() => toggleAddOn(a.key)}
                              className={`ga-addon ${on ? 'ga-addon--selected' : ''}`}
                              aria-pressed={on}
                            >
                              <span className={`ga-addon-check ${on ? 'is-on' : ''}`} aria-hidden>{on ? '✓' : ''}</span>
                              <span className="ga-addon-body">
                                <span className="ga-addon-label">{a.label}</span>
                                <span className="ga-addon-price">+{formatMoney(a.price_cents_per_guest)} <span className="ga-addon-unit">/guest</span></span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}

              <hr className="divider" />

              <div className="ga-total-block">
                <div className="row-between">
                  <span className="muted">{formatMoney(offer.price_cents)} × {headcount}</span>
                  <span style={{ fontWeight: 600 }}>{formatMoney(baseTotal)}</span>
                </div>
                {pickedAddOns.map((a) => (
                  <div key={a.key} className="row-between" style={{ fontSize: 14 }}>
                    <span className="muted">{a.label} · +{formatMoney(a.price_cents_per_guest)} × {headcount}</span>
                    <span style={{ fontWeight: 600 }}>{formatMoney(a.price_cents_per_guest * headcount)}</span>
                  </div>
                ))}
                <div className="row-between" style={{ fontSize: 22, marginTop: 6 }}>
                  <span className="display">Total</span>
                  <span className="display" style={{ color: 'var(--wine)' }}>{formatMoney(total)}</span>
                </div>
              </div>

              <hr className="divider" />

              <div>
                <div className="mono" style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 10 }}>
                  How do you want to pay?
                </div>
                <div className="ga-pay-options">
                  <PayOption
                    selected={paymentMode === 'prepaid'}
                    onClick={() => setPaymentMode('prepaid')}
                    title="Pay now"
                    headline={<>{formatMoney(offer.price_cents)}<span className="ga-pay-unit"> × {headcount}</span> = <strong>{formatMoney(total)}</strong></>}
                    bullets={[
                      'Headcount is locked in.',
                      'Cancel for a full refund before 3pm on the event day.',
                      'Best for confirmed lists.',
                    ]}
                  />
                  <PayOption
                    selected={paymentMode === 'rsvp_based'}
                    onClick={() => setPaymentMode('rsvp_based')}
                    title="Pay for who shows"
                    badge="Smarter"
                    headline={<>Up to <strong>{formatMoney(total)}</strong><span className="ga-pay-unit"> — final at 3pm</span></>}
                    bullets={[
                      'We collect RSVPs from your team.',
                      'At 3pm on the event day we charge for who\'s a yes.',
                      'You only pay for who actually shows up.',
                    ]}
                  />
                </div>
              </div>

              {error && <div className="field-error">{error}</div>}

              <button className="btn btn-pop" disabled={submitting} style={{ width: '100%', justifyContent: 'center' }}>
                {submitting ? 'Reserving…' : ctaLabel}
              </button>

              <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                {offer.window_days} · {formatTime(offer.window_start)}–{formatTime(offer.window_end)} window · Reserving for {profile?.email_domain ?? 'your team'}
              </p>
            </div>
          </form>
        </div>
      </section>
      <Footer />
      <CheckoutStyles />
      <CheckoutModal
        open={modalOpen}
        vendorName={vendor.name}
        payNow={paymentMode === 'prepaid'}
        done={insertDone}
        onFinished={onModalFinished}
      />
    </>
  );
}

function PayOption({
  selected,
  onClick,
  title,
  badge,
  headline,
  bullets,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  badge?: string;
  headline: React.ReactNode;
  bullets: string[];
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`ga-pay-option ${selected ? 'ga-pay-option--selected' : ''}`}
      aria-pressed={selected}
    >
      <div className="ga-pay-option-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className={`ga-pay-radio ${selected ? 'is-on' : ''}`} aria-hidden />
          <span className="display" style={{ fontSize: 20 }}>{title}</span>
        </div>
        {badge && <span className="ga-pay-badge">{badge}</span>}
      </div>
      <div className="ga-pay-headline">{headline}</div>
      <ul className="ga-pay-bullets">
        {bullets.map((b) => <li key={b}>{b}</li>)}
      </ul>
    </button>
  );
}

function CheckoutStyles() {
  return (
    <style>{`
      .ga-pay-options {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 14px;
      }
      .ga-pay-option {
        appearance: none;
        text-align: left;
        background: var(--bg);
        border: 1.5px solid var(--line);
        border-radius: 18px;
        padding: 18px;
        cursor: pointer;
        transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
        color: var(--ink);
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .ga-pay-option:hover {
        border-color: var(--ink);
        transform: translateY(-1px);
      }
      .ga-pay-option--selected {
        border-color: var(--ink);
        box-shadow: 0 0 0 4px color-mix(in oklch, var(--ink) 8%, transparent);
        background: var(--bg);
      }
      .ga-pay-option-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
      }
      .ga-pay-radio {
        width: 18px;
        height: 18px;
        border-radius: 999px;
        border: 1.5px solid var(--ink-soft);
        background: var(--bg);
        position: relative;
      }
      .ga-pay-radio.is-on {
        border-color: var(--ink);
      }
      .ga-pay-radio.is-on::after {
        content: '';
        position: absolute;
        inset: 3px;
        border-radius: 999px;
        background: var(--ink);
      }
      .ga-pay-badge {
        font-family: ui-monospace, 'SF Mono', Menlo, monospace;
        font-size: 10px;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        padding: 4px 8px;
        border-radius: 999px;
        background: var(--primary);
        color: var(--ink);
        font-weight: 700;
      }
      .ga-pay-headline {
        font-size: 17px;
        color: var(--ink);
      }
      .ga-pay-headline strong { color: var(--wine); }
      .ga-pay-unit { color: var(--ink-soft); font-size: 14px; }
      .ga-pay-bullets {
        margin: 4px 0 0;
        padding-left: 18px;
        color: var(--ink-soft);
        font-size: 14px;
        line-height: 1.55;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .ga-addon-list {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
      }
      .ga-addon {
        appearance: none;
        text-align: left;
        background: var(--bg);
        border: 1.5px solid var(--line);
        border-radius: 14px;
        padding: 12px 14px;
        cursor: pointer;
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 12px;
        align-items: center;
        transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
        color: var(--ink);
      }
      .ga-addon:hover { border-color: var(--ink); transform: translateY(-1px); }
      .ga-addon--selected { border-color: var(--ink); background: color-mix(in oklch, var(--primary) 18%, var(--bg)); }
      .ga-addon-check {
        width: 22px;
        height: 22px;
        border-radius: 6px;
        border: 2px solid var(--line);
        background: var(--bg);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 900;
        color: transparent;
      }
      .ga-addon-check.is-on {
        background: var(--ink);
        border-color: var(--ink);
        color: var(--bg);
      }
      .ga-addon-body { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
      .ga-addon-label { font-weight: 600; font-size: 15px; color: var(--ink); }
      .ga-addon-price { font-weight: 700; color: var(--wine); font-size: 14px; }
      .ga-addon-unit { color: var(--ink-soft); font-weight: 500; font-size: 12px; }

      .ga-total-block { display: grid; gap: 6px; }

      @media (max-width: 720px) {
        .ga-pay-options { grid-template-columns: 1fr; }
        .ga-addon-list { grid-template-columns: 1fr; }
      }
    `}</style>
  );
}
